import {createSnackbar} from 'Core/ducks/snackbar';
import {Async, AsyncResult, Loaded, Loading, ServerError, isLoaded} from 'Core/models/Async';
import {QuotingRouteConstants} from 'Quoting/Routes';
import QuotingAPI from 'Quoting/api/QuotingAPI';
import {getPlanConfigurationRequestPayload} from 'Quoting/api/apiRequestHelpers';
import {
  GpluAgeBandsAndSmokingFlags,
  RetrievePlanConfigurationRequestPayload,
  RetrievePlansRequestData,
} from 'Quoting/api/types';
import {reduce as arrReduce} from 'fp-ts/lib/Array';
import {updateDefaultValuesMapForStateAndPlan} from 'Quoting/components/QuoteForm/Plan/products/KrakenRulesAndConfig';
import {AttributeValue} from 'Quoting/components/QuoteForm/Plan/products/types';
import {runProductValidations} from 'Quoting/utils/validation';
import {getAgencyCompensation} from 'Quoting/ducks/compensation';
import {
  CreateQuoteActionType,
  addPlanToQuoteACC,
  addPlanToQuoteHI,
  calculateRates,
  changeAttributeValues,
  changeAttributeValuesByCoverage,
  changeRetrievedPlansWithCoverage,
  changeSelectedLifeCoverage,
  clearQuoteFormErrors,
  getQuoteDetails,
  initCreateQuote,
  removeQuoteFormErrorEntry,
  retrievePlanDataToAdd,
  setGpluAgeBandsAndSmokingFlags,
  setMinMaxConfig,
  updateA360Lookup,
  updateCustomerCensusClasses,
  updateDefaultValuesForSelectedPlan,
  updateIsCalculatingRates,
  updateIsLoadingQuoteForm,
  updateIsSavingQuote,
  updateMasterPolicyQuote,
  updateOffer,
  updatePlanFormLoadingState,
  updateQuote,
  updateQuoteFormErrors,
  updateQuoteRatesAndPremiumInformation,
} from 'Quoting/ducks/createQuote';
import {CompensationValue, DecisionHeapedTable} from 'Quoting/models/DTOs/CompensationPayload';
import {CensusClassDTO} from 'Quoting/models/DTOs/Opportunity';
import {
  A360Lookup,
  EntityMinMaxConfig,
  PlanWithCoverages,
  QuoteFormDropdownOption,
} from 'Quoting/models/DTOs/QuoteForm';
import {RatePremiumDetails} from 'Quoting/models/DTOs/RatePremium';
import {MasterPolicyQuote} from 'Quoting/models/DTOs/quote';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {getProductCdFromModel} from 'Quoting/utils/helpers';
import {
  extractRootIdFromEntityUri,
  isA360HealthProduct,
  isA360LifeProduct,
  isDefined,
  parsePath,
  validateHealthProductQuote,
} from 'Quoting/utils/utilities';
import {push} from 'connected-react-router';
import {fold, isRight} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {Map as ImmutableMap, fromJS} from 'immutable';
import {DateTime} from 'luxon';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {
  createOptionsForAttributes,
  createOptionsForLookup,
  enrichInitialQuote,
  enrichQuoteAfterOfferCall,
  getAvailablePlans,
  getErrorMessageFromMinMaxResult,
  getValueFromMinMaxResult,
  updateRetrievedValues,
} from './apiResponseUtilities';

function* retreivePlansWithCoveragesAPI(productCd: A360ProductModel, requestData: RetrievePlansRequestData) {
  const errorMessage = 'Error: Unable to retrieve plans.';

  try {
    yield put(changeRetrievedPlansWithCoverage(new Loading()));

    const response: AsyncResult<{plans: PlanWithCoverages[]}> = yield call(
      QuotingAPI.retrievePlansWithCoverages,
      productCd,
      requestData
    );

    if (isRight(response)) {
      yield put(
        changeRetrievedPlansWithCoverage(
          new Loaded(getAvailablePlans(response?.right?.plans, requestData.riskStateCode))
        )
      );
      yield put(
        changeSelectedLifeCoverage(
          response?.right?.plans?.[0]?.description?.toLowerCase() === 'gwl'
            ? response?.right?.plans[0].coverages?.find(c => c.applicabilityType === 'Mandatory')?.code
            : null
        )
      );
    } else {
      yield put(changeRetrievedPlansWithCoverage(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
    return response;
  } catch (error) {
    console.error(error);
    yield put(changeRetrievedPlansWithCoverage(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  }
}

function* initCreateQuoteAPI(action: ReturnType<typeof initCreateQuote>) {
  const errorMessage = 'Error: Unable to create quote.';
  try {
    yield put(updateIsLoadingQuoteForm(true));
    yield put(updateMasterPolicyQuote(new Loading()));
    const response: AsyncResult<{body: {success: MasterPolicyQuote}}> = yield call(
      QuotingAPI.initiateCreateQuote,
      action.payload.productModel,
      action.payload.createQuotePayload
    );

    if (isRight(response)) {
      const quote = response.right?.body?.success;
      yield put(
        getQuoteDetails(
          quote?._modelName as A360ProductModel,
          quote?._key?.rootId,
          quote?._key?.revisionNo,
          action.payload.opportunityId ?? ''
        )
      );
    } else {
      yield put(updateMasterPolicyQuote(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(updateMasterPolicyQuote(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(updateIsLoadingQuoteForm(false));
  }
}

function* initCreateQuoteSaga() {
  yield takeLatest(CreateQuoteActionType.INITIATE_CREATE_QUOTE, initCreateQuoteAPI);
}

function* loadA360LookupFromAPI(lookupName: string) {
  const errorMessage = 'Error: Unable to load lookup.';

  try {
    const response: AsyncResult<{body: {success: A360Lookup[]}}> = yield call(QuotingAPI.loadLookup, lookupName);
    if (isRight(response)) {
      yield put(updateA360Lookup(lookupName, createOptionsForLookup(response.right?.body?.success)));
    } else {
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

function* getQuoteDetailsFromAPI(action: ReturnType<typeof getQuoteDetails>) {
  const errorMessage = 'Error: Failed to load quote details.';

  try {
    yield put(updateIsLoadingQuoteForm(true));

    const response: AsyncResult<MasterPolicyQuote> = yield call(
      QuotingAPI.getQuoteDetails,
      action.payload.productModel,
      action.payload.rootId,
      action.payload.revisionNo
    );

    if (isRight(response)) {
      const quote = response.right;

      const lookupResponse: Array<AsyncResult<{body: {success: A360Lookup[]}}>> = yield all([
        ...([A360ProductModel.ACCMaster, A360ProductModel.CIMaster, A360ProductModel.HIMaster].includes(
          action.payload.productModel
        )
          ? [call(QuotingAPI.loadLookup, `${getProductCdFromModel(quote._modelName as A360ProductModel)}UWCompany`)]
          : []),
        call(QuotingAPI.loadLookup, 'SICCode'),
      ]);

      const maturityAgeDefaultValuesGPLU = yield call(getGPLUMaturityAgeFields, action.payload.productModel);

      const quoteToSave: Async<ImmutableMap<string, any>> = new Loaded(
        fromJS(enrichInitialQuote(quote, lookupResponse, maturityAgeDefaultValuesGPLU))
      );

      yield put(updateMasterPolicyQuote(quoteToSave));

      if (action.payload.shouldRedirect) {
        yield put(
          push(
            QuotingRouteConstants.CREATE_QUOTE(
              quote?._modelName,
              quote?._key?.rootId,
              String(quote?._key?.revisionNo)
            ).concat(action.payload.opportunityId ? `?opportunityId=${action.payload.opportunityId ?? ''}` : '')
          )
        );
      }

      const plansWithCoverages = yield call(fetchPlansWithCoverages, quote);

      const coverageCds =
        isRight(plansWithCoverages) && quote?.benefitPackagingDetail?.plans?.length > 0
          ? (plansWithCoverages.right as {plans: PlanWithCoverages[]})?.plans
              ?.find(plan => plan.code === quote?.benefitPackagingDetail?.plans?.[0]?.planCd)
              ?.coverages?.filter(cov => cov.applicabilityType === 'Mandatory' || cov.applicabilityType === 'Optional')
              ?.map(cov => cov.code)
          : undefined;

      const plansConfigRequestPayload = getPlanConfigurationRequestPayload(
        quoteToSave,
        quote?.benefitPackagingDetail?.plans?.[0]?.planCd,
        coverageCds
      );

      yield all([
        call(retrievePlanConfigurationFromAPI, plansConfigRequestPayload),
        call(
          getQuoteRatesAndPremiumFromAPI,
          quote._modelName as A360ProductModel,
          quote._key?.rootId,
          quote._key?.revisionNo
        ),
        call(fetchPlansAndLookupsAfterQuoteLoad, quote),
        call(fetchAvailableCensusClassesForCustomer, quote?.customer?._uri),
        quote._modelName === A360ProductModel.GPLUMaster && call(fetchGpluAgeBandInformation),
      ]);
      yield put(getAgencyCompensation(quote));
    } else {
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(updateIsLoadingQuoteForm(false));
  }
}

function* getGPLUMaturityAgeFields(productModel: A360ProductModel) {
  let maturityAgeDefaultValuesGPLU: {maturityAge: number; doliMaturityAge: number} = null;

  if (productModel === A360ProductModel.GPLUMaster) {
    const maturityAgeDefaultsResponse: AsyncResult<any> = yield call(
      QuotingAPI.retrieveOpenlValues,
      {productModel: A360ProductModel.GPLUMaster},
      'AttributeDefaultBehavior',
      true
    );

    if (isRight(maturityAgeDefaultsResponse)) {
      maturityAgeDefaultValuesGPLU = maturityAgeDefaultsResponse.right.behaviors.reduce((acc, currentValue) => {
        acc[currentValue.attributeName] =
          currentValue.defaultValue.result[`${currentValue.defaultValue.valueType.toLowerCase()}Value`];
        return acc;
      }, {});
    }
  }
  return maturityAgeDefaultValuesGPLU;
}

function* fetchPlansAndLookupsAfterQuoteLoad(quote: MasterPolicyQuote) {
  const productCd = getProductCdFromModel(quote?._modelName as A360ProductModel);
  yield all([
    call(loadA360LookupFromAPI, `${productCd}DeliveryModel`),
    call(loadA360LookupFromAPI, `${productCd}SitusState`),
  ]);
}

function* fetchPlansWithCoverages(quote: MasterPolicyQuote) {
  const retrievePlansRequestData: RetrievePlansRequestData = {
    productModel: quote?._modelName as A360ProductModel,
    sicCode: quote?.sicCode,
    // numberOfEligibleLives is not used for GWL Product
    numberOfEligibleLives: quote?.participationDetails?.numberOfEligibleLives,
    riskStateCode: quote?.riskStateCd,
    termEffectiveDate: quote?.termDetails?.termEffectiveDate,
    rateEffectiveDate:
      quote?.rateEffectiveDate ??
      DateTime.local()
        .toUTC()
        .toISO(),
    rateRequestDate: quote?.rateRequestDate,
  };
  return yield call(retreivePlansWithCoveragesAPI, quote?._modelName as A360ProductModel, retrievePlansRequestData);
}

function* updateQuoteAPI(action: ReturnType<typeof updateQuote>) {
  try {
    yield call(updateQuoteAction, action.payload.shouldExit, action.payload.opportunityId);
  } catch (error) {
    console.error(error);
  }
}

function* updateQuoteAction(shouldExit?: boolean, opportunityId?: string) {
  const errorMsg = 'Quote save failed.';
  try {
    yield put(updateIsSavingQuote(true));
    const {
      quote,
      quoteFormErrors,
    }: {quote: Async<ImmutableMap<string, any>>; quoteFormErrors: Record<string, string>} = yield select(
      (state: ReducerState) => state.quoteForm
    );

    if (!isLoaded(quote)) {
      throw new Error('Failed to save quote. Invalid quote data!');
    }

    const {
      isCompensationUpdated,
      compensationValue,
    }: {isCompensationUpdated: boolean; compensationValue: CompensationValue} = yield select(
      (state: ReducerState) => state.compensation
    );

    const {decisionTableHeapedResponse}: {decisionTableHeapedResponse: DecisionHeapedTable[]} = yield select(
      (state: ReducerState) => state.quoteActions
    );

    let levelEquivalent = quote?.Data?.get('levelEquivalent');

    if (compensationValue.type === 'Heaped') {
      levelEquivalent = Number(
        decisionTableHeapedResponse.find(v => v.heapedCommission === compensationValue.rate)?.levelEquivalent
      );
    }

    const response: AsyncResult<MasterPolicyQuote> = yield call(
      QuotingAPI.updateQuote,
      quote.Data.get('_modelName') as A360ProductModel,
      quote.Data.getIn(parsePath('termDetails.termCd'))
        ? quote.Data
        : quote.Data.setIn(parsePath('termDetails.termCd'), 'Perpetual'),
      {
        compensationRateImpactOccurred: isCompensationUpdated,
        levelEquivalent: compensationValue.type === 'Heaped' ? levelEquivalent : undefined,
      }
    );

    if (isRight(response)) {
      yield put(updateMasterPolicyQuote(new Loaded(fromJS(response.right as any))));
      yield put(createSnackbar('Quote saved successfully.'));

      const isCompensationAdded = isDefined(compensationValue.rate) && compensationValue.rate.trim() !== '';
      const compensationErrorKey = Object.entries(quoteFormErrors)?.find(([key, value]) =>
        value?.includes('Compensation')
      )?.[0];

      if (isCompensationAdded) {
        yield put(removeQuoteFormErrorEntry(compensationErrorKey));
      }

      if (shouldExit && opportunityId) {
        yield put(push(QuotingRouteConstants.OPPORTUNITY_SUMMARY(opportunityId)));
      }
    } else {
      yield put(createSnackbar(errorMsg));
    }
    return response;
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMsg));
  } finally {
    yield put(updateIsSavingQuote(false));

    // workaround to cleanup errors for old plan key
    yield put(updateQuoteFormErrors({}));
  }
}

function* offerAPI(action?: ReturnType<typeof updateOffer>) {
  try {
    const quote: Async<ImmutableMap<string, any>> = yield select((state: ReducerState) => state.quoteForm.quote);

    if (!isLoaded(quote)) {
      throw new Error('Failed to save quote. Invalid quote data!');
    }

    const response: AsyncResult<{body: {success: MasterPolicyQuote}}> = yield call(
      updateEntryPointVariationAPI,
      quote.Data.get('_modelName'),
      'Offer',
      quote.Data
    );

    if (isRight(response)) {
      yield put(updateMasterPolicyQuote(new Loaded(fromJS(enrichQuoteAfterOfferCall(response.right)))));
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar('Error: Failed to offer'));
  } finally {
    action?.payload?.onComplete?.();
  }
}

function* updateOfferSaga() {
  yield takeLatest(CreateQuoteActionType.UPDATE_OFFER, offerAPI);
}

function* updateEntryPointVariationAPI(
  productCd: A360ProductModel,
  entryPoint: string,
  quote: ImmutableMap<string, any>
) {
  try {
    const response: AsyncResult<{body: {success: MasterPolicyQuote}}> = yield call(
      QuotingAPI.updateEntryPointVariation,
      productCd,
      entryPoint,
      quote
    );

    return response;
  } catch (error) {
    console.error(error);
    yield put(createSnackbar('Error: Failed to update entry point variation'));
  }
}

function* updateQuoteSaga() {
  yield takeLatest(CreateQuoteActionType.UPDATE_QUOTE, updateQuoteAPI);
}

function* getQuoteSaga() {
  yield takeLatest(CreateQuoteActionType.GET_QUOTE_DETAILS, getQuoteDetailsFromAPI);
}

function* retrieveAttributeValuesFromAPI(requestPayload: RetrievePlanConfigurationRequestPayload) {
  const errorMessage = 'Error: Unable to load lookup.';

  try {
    if (Array.isArray(requestPayload.coverageCodes) && requestPayload.coverageCodes?.length > 0) {
      const fetchAttributesForAllCoverages = requestPayload.coverageCodes.map(covCd =>
        call(QuotingAPI.retrieveAttributeValues, requestPayload, covCd)
      );

      const responses: Array<AsyncResult<AttributeValue[]>> = yield all(fetchAttributesForAllCoverages);
      const successfulResponses = responses.filter(isRight);

      if (successfulResponses.length > 0) {
        if (requestPayload.productModel === A360ProductModel.GPLUMaster && requestPayload.coverageCodes) {
          const attributeValues: ImmutableMap<string, ImmutableMap<string, QuoteFormDropdownOption[]>> = pipe(
            successfulResponses,
            responseData =>
              responseData.map(res =>
                fold(
                  () => undefined,
                  (right: AttributeValue[]) => right
                )(res)
              ),
            attributeData =>
              attributeData.reduce((acc, cur, index) => {
                if (!cur) return acc;

                const coverageCode = requestPayload.coverageCodes[index];

                const innerMap = cur.reduce((innerAcc, {entityName, attributeName, valueListItems}) => {
                  const key = `${entityName}.${attributeName}`;
                  if (!innerAcc.has(key)) {
                    return innerAcc.set(key, createOptionsForAttributes(valueListItems));
                  }
                  return innerAcc;
                }, ImmutableMap<string, QuoteFormDropdownOption[]>());

                return acc.set(coverageCode, innerMap);
              }, ImmutableMap<string, ImmutableMap<string, QuoteFormDropdownOption[]>>())
          );

          yield put(changeAttributeValuesByCoverage(attributeValues));
        }
      } else {
        yield put(createSnackbar(errorMessage));
      }
    }

    const response: AsyncResult<AttributeValue[]> = yield call(QuotingAPI.retrieveAttributeValues, requestPayload);

    if (isRight(response)) {
      const attributeValues = pipe(
        response.right,
        arrReduce(
          ImmutableMap<string, QuoteFormDropdownOption[]>(),
          (acc, {entityName, attributeName, valueListItems}) => {
            const key = `${entityName}.${attributeName}`;
            return acc.set(key, createOptionsForAttributes(valueListItems));
          }
        )
      );

      yield put(changeAttributeValues(updateRetrievedValues(attributeValues, requestPayload.planCd)));
    } else {
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

function* retrievePlanDefaultValuesFromAPI(requestPayload: RetrievePlanConfigurationRequestPayload) {
  const errorMessage = 'Error: failed to retrieve default values for the selected plan.';

  try {
    let defaultValuesMapForSelectedPlan: ImmutableMap<string, ImmutableMap<string, any[]>>;
    const shouldFetchDefaultsPerCoverage = isA360LifeProduct(requestPayload.productModel);
    if (Array.isArray(requestPayload.coverageCodes) && shouldFetchDefaultsPerCoverage) {
      const fetchDefaultValuesForAllCoverages = requestPayload.coverageCodes.map(covCd =>
        call(QuotingAPI.retrieveOpenlValues, requestPayload, 'AttributeDefaultBehavior', false, covCd)
      );
      const responses: Array<AsyncResult<any[]>> = yield all(fetchDefaultValuesForAllCoverages);
      const successfulResponses = responses.filter(isRight);

      if (successfulResponses.length > 0) {
        defaultValuesMapForSelectedPlan = pipe(
          successfulResponses,
          responseData =>
            responseData.map(res =>
              fold(
                () => undefined,
                (right: any[]) => right
              )(res)
            ),
          defaultData =>
            defaultData.reduce((acc: any, covBehvs: any, index) => {
              if (!covBehvs) return acc;

              const coverageCode = requestPayload.coverageCodes[index];

              const innerMap = fromJS(
                covBehvs.behaviors.reduce((innerAcc, {entityName, attributeName, defaultValue}) => {
                  const key = `${entityName}.${attributeName}`;
                  const value = defaultValue.result[`${defaultValue.result.valueType.toLowerCase()}Value`];
                  return innerAcc.set(key, value);
                }, ImmutableMap<string, any[]>())
              );

              return acc.set(coverageCode, innerMap);
            }, ImmutableMap<string, ImmutableMap<string, any[]>>())
        );
      } else {
        yield put(createSnackbar(errorMessage));
      }
    } else {
      const response: AsyncResult<any> = yield call(
        QuotingAPI.retrieveOpenlValues,
        requestPayload,
        'AttributeDefaultBehavior'
      );

      if (isRight(response)) {
        defaultValuesMapForSelectedPlan =
          isDefined(response.right) && Array.isArray(response.right.behaviors)
            ? fromJS(
                response.right.behaviors.reduce((acc: any, behavior: any) => {
                  const key = `${behavior.entityName}.${behavior.attributeName}`;
                  if (!acc[key]) {
                    acc[key] =
                      behavior.defaultValue.result[`${behavior.defaultValue.result.valueType.toLowerCase()}Value`];
                  }
                  return acc;
                }, {})
              )
            : ImmutableMap();
      } else {
        yield put(createSnackbar(errorMessage));
      }
    }

    const resetBehaviorResponse: AsyncResult<any> = yield call(
      QuotingAPI.retrieveOpenlValues,
      requestPayload,
      'AttributeResetBehavior'
    );

    let isPortabilityApplied = false;
    if (isRight(resetBehaviorResponse)) {
      isPortabilityApplied = Boolean(
        resetBehaviorResponse?.right?.behaviors?.find(
          (b: any) =>
            b.entityName === `${getProductCdFromModel(requestPayload.productModel)}BenefitStructure` &&
            b.attributeName === 'isPortabilityApplied'
        )?.resetValue?.result?.booleanValue
      );
    }

    yield put(
      updateDefaultValuesForSelectedPlan(
        requestPayload.planCd ?? 'DefaultWithoutPlan',
        updateDefaultValuesMapForStateAndPlan(
          defaultValuesMapForSelectedPlan,
          requestPayload.situsStateCd,
          requestPayload.planCd,
          isPortabilityApplied
        )
      )
    );
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

function* retrievePlanConfigurationFromAPI(
  requestPayload?: RetrievePlanConfigurationRequestPayload,
  isAddingNewPlan: boolean = false
) {
  const errorMessage = 'Error: failed to retrieve plan configuration.';
  try {
    yield all([
      call(retrieveAttributeValuesFromAPI, requestPayload),
      call(retrievePlanDefaultValuesFromAPI, requestPayload),
      requestPayload.productModel === A360ProductModel.GPLUMaster && call(retrieveMinMaxValuesFromAPI, requestPayload),
    ]);
    if (isAddingNewPlan) {
      yield put(updatePlanFormLoadingState(true));

      if (requestPayload?.planCd === 'GAI7') {
        yield put(addPlanToQuoteACC(requestPayload?.planCd ?? ''));
      } else {
        yield put(addPlanToQuoteHI(requestPayload?.planCd ?? ''));
        yield call(offerAPI);
      }
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(updatePlanFormLoadingState(false));
  }
}

function* fetchAvailableCensusClassesForCustomer(customerURI?: string) {
  const errorMessage = 'Failed to retrieve list of census classes.';
  try {
    if (customerURI) {
      const response: AsyncResult<CensusClassDTO[]> = yield call(
        QuotingAPI.fetchCustomerCensusClasses,
        extractRootIdFromEntityUri(customerURI)
      );
      if (isRight(response) && Array.isArray(response?.right)) {
        yield put(updateCustomerCensusClasses(response.right));
      } else {
        yield put(updateCustomerCensusClasses([]));
        yield put(createSnackbar(errorMessage));
      }
    } else {
      yield put(updateCustomerCensusClasses([]));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(updateCustomerCensusClasses([]));
    yield put(createSnackbar(errorMessage));
  }
}

function* calculateRatesFromAPI(action: ReturnType<typeof calculateRates>) {
  const errorMessage = 'Failed to calculate rates. Please review and fix the errors.';
  try {
    yield put(updateIsCalculatingRates(true));
    const quoteSaveResponse: AsyncResult<MasterPolicyQuote> = yield call(updateQuoteAction);
    const compensationValue: CompensationValue = yield select(
      (state: ReducerState) => state.compensation.compensationValue
    );

    const {
      minMaxConfig,
      quoteFormErrors,
    }: {
      minMaxConfig: Record<string, EntityMinMaxConfig>;
      quoteFormErrors: Record<string, string | undefined>;
    } = yield select((state: ReducerState) => state.quoteForm);

    if (isRight(quoteSaveResponse)) {
      const validationResults = validateQuoteBeforeRating(
        quoteSaveResponse.right,
        compensationValue,
        action.payload.useEISValidation,
        minMaxConfig
      );

      if (
        Object.values(quoteFormErrors).some(value => isDefined(value)) ||
        Object.values(validationResults).some(value => isDefined(value))
      ) {
        yield put(updateQuoteFormErrors(validationResults));
        yield put(createSnackbar(errorMessage));
        return true;
      }

      const gpluAgeBandsAndSmokingFlags: GpluAgeBandsAndSmokingFlags = yield select(
        (state: ReducerState) => state.quoteForm.gpluAgeBandsAndSmokingFlags
      );

      const quoteRatesResponse: AsyncResult<RatePremiumDetails> = yield call(
        QuotingAPI.getQuoteRatesAndPremium,
        action.payload.quote.get('_modelName') as A360ProductModel,
        action.payload.quote.getIn(parsePath('_key.rootId')) as string,
        action.payload.quote.getIn(parsePath('_key.revisionNo')) as number
      );

      const quoteRates = isRight(quoteRatesResponse) ? quoteRatesResponse.right : undefined;

      const response: AsyncResult<any> = yield call(
        QuotingAPI.calculateRates,
        fromJS(quoteSaveResponse.right),
        action.payload.customerCensusClasses,
        quoteRates,
        gpluAgeBandsAndSmokingFlags
      );
      if (isRight(response)) {
        yield put(clearQuoteFormErrors());
        yield put(updateQuoteRatesAndPremiumInformation(response.right));
        const productModel = action.payload.quote.get('_modelName') as A360ProductModel;
        const rootId = action.payload.quote.getIn(parsePath('_key.rootId')) as string;
        const revisionNo = action.payload.quote.getIn(parsePath('_key.revisionNo')) as number;

        const quoteAndRatePremiumUpdatedResponse: Array<AsyncResult<any>> = yield all([
          call(QuotingAPI.getQuoteDetails, productModel, rootId, revisionNo),
          call(getQuoteRatesAndPremiumFromAPI, productModel, rootId, revisionNo),
        ]);

        if (isRight(quoteAndRatePremiumUpdatedResponse[0])) {
          yield put(updateMasterPolicyQuote(new Loaded(fromJS(quoteAndRatePremiumUpdatedResponse[0].right))));
        }
      } else {
        const getQuoteDetailsRes: AsyncResult<MasterPolicyQuote> = yield call(
          QuotingAPI.getQuoteDetails,
          action.payload.quote.get('_modelName') as A360ProductModel,
          action.payload.quote.getIn(parsePath('_key.rootId')) as string,
          action.payload.quote.getIn(parsePath('_key.revisionNo')) as number
        );

        if (isRight(getQuoteDetailsRes)) {
          yield put(updateMasterPolicyQuote(new Loaded(fromJS(getQuoteDetailsRes.right as any))));
        }
        yield put(createSnackbar(errorMessage));
        yield put(updateQuoteFormErrors(response.left));
        yield put(updateQuoteRatesAndPremiumInformation(undefined));
      }
    }
  } catch (error) {
    console.error(error);
    yield put(updateQuoteRatesAndPremiumInformation(undefined));
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(updateIsCalculatingRates(false));
  }
}

function* addNewPlan(action: ReturnType<typeof retrievePlanDataToAdd>) {
  const errorMessage = 'Failed to add a plan.';
  try {
    yield put(updatePlanFormLoadingState(true));
    yield call(retrievePlanConfigurationFromAPI, action.payload.requestPayload, true);
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(updatePlanFormLoadingState(false));
  }
}

function* getQuoteRatesAndPremiumFromAPI(productCd: A360ProductModel, rootId: string, revisionNo: number) {
  const errorMessage = 'Failed to retrieve quote rates and premium details.';
  try {
    const response: AsyncResult<RatePremiumDetails> = yield call(
      QuotingAPI.getQuoteRatesAndPremium,
      productCd,
      rootId,
      revisionNo
    );
    if (isRight(response)) {
      yield put(updateQuoteRatesAndPremiumInformation(response.right));
    } else {
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

function* calculateRatesSaga() {
  yield takeLatest(CreateQuoteActionType.CALCULATE_RATES, calculateRatesFromAPI);
}

function* retreivePlanDataToAddSaga() {
  yield takeLatest(CreateQuoteActionType.RETRIEVE_PLAN_INFORMATION_TO_ADD, addNewPlan);
}

function* retrievePlanConfigurationSaga() {
  yield takeLatest(CreateQuoteActionType.RETRIEVE_PLAN_CONFIGURATION, retrievePlanConfigurationFromAPI);
}

function* editQuotePlanName() {
  const errorMessage = 'Failed to update plan.';

  try {
    yield put(updateIsLoadingQuoteForm(true));
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(updateIsLoadingQuoteForm(false));
  }
}

function* editQuotePlanNameSaga() {
  yield takeLatest(CreateQuoteActionType.EDIT_PLAN_NAME, editQuotePlanName);
}

function* retrieveMinMaxValuesFromAPI(requestPayload: RetrievePlanConfigurationRequestPayload) {
  const errorMessage = 'Failed to retrieve min-max config from API.';
  try {
    if (!requestPayload.coverageCodes) return;
    const fetchMinMaxForAllCoverages = requestPayload.coverageCodes?.map(covCd =>
      call(QuotingAPI.retrieveMinMaxConfigFromOpenL, requestPayload, covCd)
    );

    const responses: Array<AsyncResult<any>> = yield all(fetchMinMaxForAllCoverages);

    const minMaxConfig: Record<string, any> = pipe(
      responses,
      responseData =>
        responseData.map(res =>
          fold(
            () => {},
            (right: any) => right
          )(res)
        ),
      minMaxConfigForCoverage =>
        minMaxConfigForCoverage.reduce((acc: Record<string, any>, cur, index) => {
          acc[requestPayload.coverageCodes[index]] = cur.behaviors.reduce((minMaxResults, cov) => {
            minMaxResults[`${cov.entityName}.${cov.attributeName}`] = {
              min: getValueFromMinMaxResult(cov.min),
              max: getValueFromMinMaxResult(cov.max),
              increment: getValueFromMinMaxResult(cov.increment),
              message: getErrorMessageFromMinMaxResult(cov.message, cov.min),
            };
            return minMaxResults;
          }, {});
          return acc;
        }, {})
    );
    yield put(setMinMaxConfig(minMaxConfig));
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchGpluAgeBandInformation() {
  const errorMessage = 'Failed to retrieve age band information';

  try {
    const response: AsyncResult<GpluAgeBandsAndSmokingFlags> = yield call(QuotingAPI.getGPLUAgeBands);

    if (isRight(response)) {
      yield put(setGpluAgeBandsAndSmokingFlags(response.right));
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

const validateQuoteBeforeRating = (
  quote: MasterPolicyQuote,
  compensationValue: Record<string, any>,
  useEISValidation: boolean,
  minMaxConfig: Record<string, EntityMinMaxConfig>
) => {
  const productModel = quote?._modelName as A360ProductModel;

  if (isA360HealthProduct(productModel)) {
    return validateHealthProductQuote(
      quote as any,
      true,
      isDefined(compensationValue.rate) && compensationValue.rate.trim() !== '',
      useEISValidation
    );
  }

  if (isA360LifeProduct(productModel)) {
    return runProductValidations(fromJS(quote), productModel, minMaxConfig);
  }
};

export default function* quoteFormSaga() {
  yield all([
    initCreateQuoteSaga(),
    getQuoteSaga(),
    retrievePlanConfigurationSaga(),
    updateQuoteSaga(),
    calculateRatesSaga(),
    retreivePlanDataToAddSaga(),
    updateOfferSaga(),
    editQuotePlanNameSaga(),
  ]);
}
